import Link from "next/link"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"

import {
  notebookPeriodLabel,
  notebookScheduleLabel,
  type NotebookRow,
} from "../utils/notebookSeries"
import { formatDate } from "../utils/reportClient"

export function NotebookSeriesDialog({
  row,
  onClose,
  hrefFor,
  onEditSchedule,
  onShare,
  onEditSlack,
}: {
  row: NotebookRow | null
  onClose: () => void
  hrefFor: (id: string) => string
  onEditSchedule?: (row: NotebookRow) => void
  onShare?: (row: NotebookRow) => void
  onEditSlack?: (row: NotebookRow) => void
}) {
  const schedule = row?.schedule
  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent
        size="lg"
        className="max-h-[85vh] max-w-[calc(100vw-2rem)] sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>{row?.title ?? "Notebook editions"}</DialogTitle>
          <DialogDescription>
            {row ? notebookScheduleLabel(row) : ""}. Each edition is a separate
            notebook
            {row?.seriesStorage === "chat"
              ? "."
              : " covering its reporting period."}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {row && (onEditSchedule || onShare || onEditSlack) && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {onEditSchedule && (
                <Button variant="secondary" onClick={() => onEditSchedule(row)}>
                  Edit schedule
                </Button>
              )}
              {onShare && (
                <Button variant="secondary" onClick={() => onShare(row)}>
                  Share series
                </Button>
              )}
              {onEditSlack && (
                <Button variant="secondary" onClick={() => onEditSlack(row)}>
                  Slack notifications
                </Button>
              )}
            </div>
          )}
          {schedule?.enabled &&
            row?.schedulingAvailable !== false &&
            schedule.execution_mode !== "manual" &&
            schedule.status !== "manual_only" &&
            schedule.next_run_at && (
              <p className="text-sm text-coval-text-muted">
                Next run {formatDate(schedule.next_run_at, true)} Pacific
              </p>
            )}
          {schedule?.last_run?.status === "failed" && (
            <p role="alert" className="text-sm text-coval-danger-text">
              Last run failed
              {schedule.last_run.error ? `: ${schedule.last_run.error}` : "."}
            </p>
          )}
          <div className="divide-y divide-coval-border">
            {row?.editions?.map((edition) => (
              <Link
                key={edition.id}
                href={hrefFor(edition.id)}
                className="block py-3 text-coval-text hover:underline"
              >
                <div className="font-medium">{edition.title}</div>
                <div className="text-sm text-coval-text-muted">
                  {edition.period
                    ? `Covers ${notebookPeriodLabel(edition.period)} · `
                    : ""}
                  Saved {formatDate(edition.created_at, true)} Pacific
                </div>
              </Link>
            ))}
          </div>
          {!row?.editions?.length && (
            <p className="text-sm text-coval-text-muted">
              {row?.historyPartial
                ? "No editions are available in this notebook list."
                : "No scheduled editions yet."}
            </p>
          )}
          {row?.historyPartial && Boolean(row.editions?.length) && (
            <p className="text-sm text-coval-text-muted">
              Some editions are not available in this notebook list.
            </p>
          )}
          {row?.template && (
            <Link
              href={hrefFor(row.template.id)}
              className="text-sm text-coval-text hover:underline"
            >
              Open original notebook: {row.template.title}
            </Link>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
